import { fn } from '@ember/helper';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { and, eq, not } from 'ember-truth-helpers';
import BvBtn from 'volta/components/bv-btn';
import classNames from 'volta/helpers/class-names';
import guid from 'volta/helpers/guid';
import orElse from 'volta/helpers/or-else';
import routeIsActive from 'volta/helpers/route-is-active';

import type { TOC } from '@ember/component/template-only';
import type { THue } from 'volta/utils/style-utils';
import type { BvBtnArgs } from 'volta/components/bv-btn';

interface BvTabsTabArgs extends Omit<BvBtnArgs, 'onClick'> {
  expand?: boolean;
  focusFn?: (id?: string) => void;
  onClick?: (id: string, event?: Event) => void;
  id?: string;
  selectedTab?: string;
  disabled?: boolean;
  alignEnd?: boolean;
  iconLeftColor?: THue;
  iconRightColor?: THue;
}

export interface BvTabsTabSignature {
  Args: BvTabsTabArgs;
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

const BvTabsTabComponent: TOC<BvTabsTabSignature> = <template>
  {{#let (orElse @id (guid)) as |id|}}
    {{#let
      (if @route (routeIsActive @route) (eq @selectedTab id))
      (if
        (and (not @route) (not @selectedTab) (not @disabled)) (fn (optional @focusFn) id) (optional)
      )
      as |isSelected focusFn|
    }}
      <div
        role='tab'
        data-test-tab={{id}}
        class={{classNames
          'bv-tab'
          (if isSelected 'bv-tab--is-selected')
          (if @disabled 'bv-tab--is-disabled')
          (if @alignEnd 'bv-tab--align-end')
        }}
      >
        {{#let
          (component
            BvBtn
            size=@size
            route=@route
            routeModel=@routeModel
            routeQuery=@routeQuery
            iconLeft=@iconLeft
            iconLeftColor=@iconLeftColor
            iconRight=@iconRight
            iconRightColor=@iconRightColor
            subtle=true
            fullWidth=@expand
            text=@text
            pressed=isSelected
            disabled=@disabled
            onClick=(fn (optional @onClick) id)
            testName=id
          )
          as |TabBtn|
        }}
          {{#if (has-block)}}
            {{! template-lint-disable no-at-ember-render-modifiers }}
            <TabBtn
              id={{id}}
              role='presentation'
              data-test-tab-url={{@route}}
              class='bv-tab__button'
              ...attributes
              {{didInsert focusFn}}
            >
              {{yield}}
            </TabBtn>
          {{else}}
            <TabBtn
              id={{id}}
              role='presentation'
              data-test-tab-url={{@route}}
              class='bv-tab__button'
              ...attributes
              {{didInsert focusFn}}
            />
          {{/if}}
        {{/let}}
      </div>
    {{/let}}
  {{/let}}
</template>;

export default BvTabsTabComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvTabs::Tab': typeof BvTabsTabComponent;
    'bv-tabs/tab': typeof BvTabsTabComponent;
  }
}
